import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { getPublicAgents } from '@/services/api';

function humanizePosition(pos = '') {
  return pos.replace(/([A-Z])/g, ' $1').trim();
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const AgentCardSkeleton = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start gap-4">
      <Skeleton className="h-14 w-14 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
    <div className="mt-4 flex gap-2">
      <Skeleton className="h-6 w-24 rounded-full" />
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
    <Skeleton className="mt-4 h-9 w-full rounded-full" />
  </div>
);

const AgentCard = ({ agent }) => {
  const initials = getInitials(agent.name);
  const location = [agent.brokerage_city, agent.brokerage_state].filter(Boolean).join(', ');

  return (
    <div className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to={`/agents/${agent.ref}`}
            className="font-semibold text-slate-900 hover:text-blue-700 transition-colors line-clamp-1"
          >
            {agent.name}
          </Link>
          <p className="mt-0.5 text-sm text-slate-500 line-clamp-1">{humanizePosition(agent.position)}</p>
          <p className="mt-0.5 text-sm text-slate-600 font-medium line-clamp-1">{agent.brokerage_name}</p>
        </div>
      </div>

      {location && (
        <p className="mt-3 flex items-center gap-1 text-sm text-slate-500">
          <MapPin size={14} className="shrink-0 text-slate-400" />
          {location}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-100">
          {agent.active_count} active
        </span>
        {agent.sold_count > 0 && (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
            {agent.sold_count} sold
          </span>
        )}
      </div>

      <Button asChild size="sm" className="mt-4 w-full rounded-full">
        <Link to={`/agents/${agent.ref}`}>View profile</Link>
      </Button>
    </div>
  );
};

export default function Agents() {
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [agents, setAgents] = useState([]);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const debounceRef = useRef(null);
  const searchRef = useRef({ q: '', city: '' });

  const fetchAgents = useCallback(async ({ q: qv, city: cv, page: pg, append = false }) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);
    const data = await getPublicAgents({ q: qv, city: cv, page: pg, perPage: 24 });
    if (!append) {
      setAgents(data.results || []);
    } else {
      setAgents((prev) => [...prev, ...(data.results || [])]);
    }
    setNumPages(data.num_pages || 1);
    setPage(data.page || pg);
    setLoading(false);
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    fetchAgents({ q: '', city: '', page: 1 });
  }, [fetchAgents]);

  const triggerSearch = useCallback((qv, cv) => {
    searchRef.current = { q: qv, city: cv };
    setPage(1);
    fetchAgents({ q: qv, city: cv, page: 1, append: false });
  }, [fetchAgents]);

  const handleQChange = (e) => {
    const val = e.target.value;
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSearch(val, city), 350);
  };

  const handleCityChange = (e) => {
    const val = e.target.value;
    setCity(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSearch(q, val), 350);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    triggerSearch(q, city);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    fetchAgents({ q: searchRef.current.q, city: searchRef.current.city, page: nextPage, append: true });
  };

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">Find a real estate agent</h1>
          <p className="mx-auto mt-3 max-w-xl text-white/85">Connect with experienced agents in your area who know the local market.</p>
          <form onSubmit={handleSearchSubmit} className="mx-auto mt-8 max-w-2xl">
            <div className="flex flex-col sm:flex-row gap-2 rounded-2xl bg-white p-2 shadow-lg">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={handleQChange}
                  placeholder="Name or brokerage…"
                  className="pl-9 border-0 focus-visible:ring-0 rounded-xl bg-transparent"
                />
              </div>
              <div className="relative flex-1">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={city}
                  onChange={handleCityChange}
                  placeholder="City…"
                  className="pl-9 border-0 focus-visible:ring-0 rounded-xl bg-transparent"
                />
              </div>
              <Button type="submit" className="rounded-xl px-6 shrink-0">Search</Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!loading && (q || city) && (
          <p className="mb-6 text-sm text-slate-600">
            Showing agents
            {q && <> matching &ldquo;{q}&rdquo;</>}
            {city && <> in {city}</>}
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <AgentCardSkeleton key={i} />)}
          </div>
        ) : agents.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No agents found"
            description="Try a different name, brokerage, or city."
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((a) => <AgentCard key={a.ref} agent={a} />)}
            </div>

            {page < numPages && (
              <div className="mt-10 flex justify-center">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="rounded-full px-8"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
